// SCR-233's delivery log for one webhook (API-235): the last 30 days, newest first, a page at a
// time (keyset cursor) — event, status, attempts, the receiver's last status code, a content-free
// error class and the times. Rows carry no event content.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { ApiError } from "../../api/errors";
import { Chip } from "../../primitives/page";
import { Empty } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import { asApiError, deliveryRow, isNotAvailable, loadDeliveries, NOT_AVAILABLE, type DeliveryRow, type NotificationWebhook, type WebhookDelivery } from "./model";

const COLUMNS: readonly Column<DeliveryRow>[] = [
  { key: "created", header: "Event at", cell: (row) => row.created },
  { key: "event", header: "Event", help: "notifications.events", cell: (row) => row.event },
  { key: "status", header: "Status", help: "notifications.deliveryStatus", cell: (row) => <Chip tone={row.statusTone}>{row.statusLabel}</Chip> },
  { key: "attempts", header: "Attempts", help: "notifications.attempts", numeric: true, cell: (row) => row.attempts },
  { key: "lastStatus", header: "Last answer", numeric: true, cell: (row) => row.lastStatus },
  { key: "errorClass", header: "Last error", help: "notifications.errorClass", cell: (row) => row.errorClass },
  { key: "delivered", header: "Delivered", cell: (row) => row.delivered },
  { key: "nextAttempt", header: "Next try", cell: (row) => row.nextAttempt },
];

interface DeliveriesState {
  items: WebhookDelivery[];
  nextCursor: string | null;
  loading: boolean;
  unavailable: boolean;
  error: ApiError | null;
}

const START: DeliveriesState = { items: [], nextCursor: null, loading: true, unavailable: false, error: null };

export function DeliveriesList({ webhook }: { webhook: NotificationWebhook }) {
  const client = useApi();
  const [state, setState] = useState<DeliveriesState>(START);

  const fetchPage = (cursor: string | null, previous: WebhookDelivery[]) => {
    setState((current) => ({ ...current, loading: true, error: null }));
    loadDeliveries(client, webhook, cursor)
      .then((page) => setState({ items: [...previous, ...page.items], nextCursor: page.nextCursor, loading: false, unavailable: false, error: null }))
      .catch((error: unknown) =>
        setState((current) => ({ ...current, loading: false, unavailable: isNotAvailable(error), error: isNotAvailable(error) ? null : asApiError(error) })),
      );
  };
  const reload = () => {
    setState(START);
    fetchPage(null, []);
  };
  useEffect(reload, [client, webhook.orgId, webhook.id]);

  if (state.unavailable) return <Empty message={NOT_AVAILABLE} />;
  return (
    <DataTable
      caption={`Deliveries of ${webhook.name}`}
      hideCaption
      columns={COLUMNS}
      rows={state.items.map(deliveryRow)}
      rowKey={(row) => row.id}
      loading={state.loading}
      error={state.error}
      onRetry={reload}
      emptyMessage="No deliveries in the last 30 days."
      nextCursor={state.nextCursor}
      onLoadMore={() => fetchPage(state.nextCursor, state.items)}
    />
  );
}
